import { memo, useCallback, useMemo, useState, useRef } from 'react';
import { City } from 'country-state-city';
import {
  Button,
  Form,
  Input,
  Row,
  Col,
  Radio,
  Slider,
  InputNumber,
  AutoComplete,
  Select,
} from 'antd';
import type { FormInstance } from 'antd';
import { DollarOutlined } from '@ant-design/icons';

import TagMaker from '../../../../components/common/tag-maker';
import Editor from '../../../../components/editor';

import { Step, WorkplaceType } from '../../types/index.types';
import { COUNTRY_OPTIONS } from '../../utils/jobLocation';

interface Props {
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  form: FormInstance<any>;
  onStepChange?: (step: Step) => void;
  onGetAutoPopulatedValues?: (title: string, description: string) => void;
}

const usCities = City.getCitiesOfCountry('US') || [];

const options = usCities.map((city) => ({
  value: `${city.name}, ${city.stateCode}`,
}));

const JobDetailsStep = ({
  form,
  onStepChange,
  onGetAutoPopulatedValues,
}: Props) => {
  const [salaryMin, setSalaryMin] = useState<number>(
    form.getFieldValue('minSalary') || 50000,
  );
  const [salaryMax, setSalaryMax] = useState<number>(
    form.getFieldValue('maxSalary') || 100000,
  );

  const prevDescriptionRef = useRef<string | undefined>(
    form.getFieldValue('description'),
  );

  const handleNext = useCallback(async () => {
    try {
      await form.validateFields();
      onStepChange && onStepChange(Step.SkillsEducation);
    } catch (errorInfo) {
      console.error('Validation failed:', errorInfo);
    }
  }, [form, onStepChange]);

  const handleSliderChange = useCallback(
    (value: number[]) => {
      setSalaryMin(value[0]);
      setSalaryMax(value[1]);
      form.setFieldsValue({
        minSalary: value[0],
        maxSalary: value[1],
      });
    },
    [setSalaryMin, setSalaryMax, form],
  );

  const handleSliderMinChange = useCallback(
    (value: number | null) => {
      setSalaryMin(value ? value : 0);
      form.setFieldsValue({
        minSalary: value ? value : 0,
      });
    },
    [setSalaryMin, form],
  );

  const handleSliderMaxChange = useCallback(
    (value: number | null) => {
      setSalaryMax(value ? value : 0);
      form.setFieldsValue({
        maxSalary: value ? value : 0,
      });
    },
    [setSalaryMax, form],
  );

  const handleEditorChange = useCallback(
    (value: string) => {
      form.setFieldsValue({
        description: value,
      });
    },
    [form],
  );

  const handleEditorBlur = useCallback(() => {
    const description = form.getFieldValue('description');

    if (!description) return;

    if (description === prevDescriptionRef.current) return;

    prevDescriptionRef.current = description;

    if (onGetAutoPopulatedValues) {
      const title = form.getFieldValue('title');
      onGetAutoPopulatedValues(title, description);
    }
  }, [form, onGetAutoPopulatedValues]);

  const handleAltJobTitlesChange = useCallback(
    (value: string[]) => {
      form.setFieldsValue({
        altJobTitles: value,
      });
    },
    [form],
  );

  const title = useMemo(() => {
    return (
      <Form.Item name="title" label="Job title" rules={[{ required: true }]}>
        <Input size="large" />
      </Form.Item>
    );
  }, []);

  const alternativeJobTitles = useMemo(() => {
    return (
      <Form.Item
        name="altJobTitles"
        label="Alternative job titles"
        tooltip="Will be used for candidates search with these titles as well"
      >
        <TagMaker
          value={form.getFieldValue('altJobTitles')}
          limit={5}
          label={'Add new job title'}
          onChange={handleAltJobTitlesChange}
        />
      </Form.Item>
    );
  }, [form]);

  const description = useMemo(() => {
    return (
      <Form.Item
        name="description"
        label="Job description"
        rules={[{ required: true }]}
      >
        <Editor
          value={form.getFieldValue('description')}
          onChange={handleEditorChange}
          onBlur={handleEditorBlur}
        />
      </Form.Item>
    );
  }, [handleEditorChange, handleEditorBlur]);

  const companyAndLocation = useMemo(() => {
    return (
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="employerName" label="Company name">
            <Input size="large" placeholder="Company name" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="address" label="Location">
            <AutoComplete
              options={options}
              size="large"
              placeholder="Address"
              filterOption={(inputValue, option) =>
                (option?.value ?? '')
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="countryCode"
                label="Country"
                extra="Where the job is performed. Used to ask candidates the right work-authorisation questions. Leave empty if you are not sure."
              >
                <Select
                  showSearch
                  size="large"
                  placeholder="Select a country"
                  options={COUNTRY_OPTIONS}
                  optionFilterProp="label"
                  aria-label="Country"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="city" label="City">
                <Input size="large" placeholder="City" maxLength={255} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="state" label="State / region">
                <Input
                  size="large"
                  placeholder="State or region"
                  maxLength={255}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  }, []);

  const employmentType = useMemo(() => {
    return (
      <Form.Item name="workplaceTypes" label="Employment type">
        <Radio.Group>
          <Radio value={WorkplaceType.HYBRID}>Hybrid</Radio>
          <Radio value={WorkplaceType.REMOTE}>Remote</Radio>
          <Radio value={WorkplaceType.ONSITE}>Onsite</Radio>
        </Radio.Group>
      </Form.Item>
    );
  }, [form]);

  const salaryrange = useMemo(() => {
    return (
      <Form.Item name="salaryRange" label="Salary range">
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="minSalary" style={{ margin: 0 }}>
              <InputNumber
                placeholder="Min"
                prefix={<DollarOutlined />}
                value={salaryMin}
                max={1000000}
                min={0}
                size="large"
                style={{ width: '100%' }}
                onChange={handleSliderMinChange}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="maxSalary" style={{ margin: 0 }}>
              <InputNumber
                placeholder="Max"
                prefix={<DollarOutlined />}
                value={salaryMax}
                max={1000000}
                min={0}
                size="large"
                style={{ width: '100%' }}
                onChange={handleSliderMaxChange}
              />
            </Form.Item>
          </Col>
        </Row>
        <Slider
          range
          max={1000000}
          min={0}
          value={[salaryMin, salaryMax]}
          onChange={handleSliderChange}
        />
      </Form.Item>
    );
  }, [
    salaryMin,
    salaryMax,
    handleSliderMaxChange,
    handleSliderMinChange,
    handleSliderChange,
  ]);

  return (
    <>
      {title}
      {alternativeJobTitles}
      {description}
      {companyAndLocation}
      {employmentType}
      {salaryrange}
      {onStepChange && (
        <Form.Item>
          <Button
            type="primary"
            size="large"
            block
            shape="round"
            onClick={handleNext}
          >
            Next
          </Button>
        </Form.Item>
      )}
    </>
  );
};

export default memo(JobDetailsStep);
